import { useState, useCallback, useEffect } from 'react';
import type * as React from 'react';
import { debounce } from 'lodash-es';

export function useWidth(elementRef: React.RefObject<HTMLElement>, initialWidth = 0): [number] {
  const [elementWidth, setElementWidth] = useState(initialWidth);

  const updateWidth = useCallback(() => {
    if (elementRef && elementRef.current) {
      const { width } = elementRef.current.getBoundingClientRect();
      setElementWidth(width);
    }
  }, [elementRef]);

  useEffect(() => {
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [updateWidth, initialWidth]);

  return [elementWidth];
}

/**
 * @param {React.RefObject<HTMLElement>} [elementRef] Ref object for the element.
 * @param {number} [initialWidth=0] Initial width of the element.
 */
export function useWidthChanged(
  elementRef: React.RefObject<HTMLElement>,
  initialWidth = 0,
): [number] {
  const [elementWidth, setElementWidth] = useState(initialWidth);

  useEffect(() => {
    const updateWidth = debounce(() => {
      if (elementRef && elementRef.current) {
        const { width } = elementRef.current.getBoundingClientRect();
        setElementWidth(width);
      }
    }, 100);

    updateWidth();

    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [initialWidth, elementRef]);

  return [elementWidth];
}
